/**
 * MandatsaufloesungPage - Administration › Mandatsauflösung (Stefan P6 V1.0 Kap. 17.3).
 *
 * The tenant's rules of the Mandatsauflösung: the Fristregel for a Kündigung without a date (sofort,
 * Monatsende, Quartalsende, x Tage; counted from the Eingangsdatum), the lead of the Übergabe-Aufgabe,
 * the portal run-on after the Enddatum and the Aufbewahrung. A Rückdatierung is an Enddatum before the
 * Eingangsdatum (no tolerance setting). The Austragungs-Fristen of the insurers stay on the Versicherer-Fristen page.
 * Read tenant.read, save tenant.manage (the api is the hard line; the bounds mirror its DTO).
 */

import { useState } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FRISTREGEL_VALUES, MANDATE_TERMINATION_CONFIG_BOUNDS as B } from '@brokly/shared';
import type { Fristregel, MandateTerminationConfigDto } from '@brokly/shared';
import { Button } from '../../../components/ui/Button';
import { apiClient } from '../../../lib/api-client';
import type { SetMandateTerminationConfigBody } from '../../../lib/api-client';
import { qk } from '../../../lib/query-keys';
import { AdminPageHeader } from '../AdminPageHeader';
import { ADMIN_PAGE_META } from '../adminNav';
import { useEffectivePermissions } from '../useEffectivePermissions';
import { useTextTemplates } from '../hooks';
import { TextTemplatesEditor } from './MahnwesenPage';

const meta = ADMIN_PAGE_META.mandatsaufloesung;

const FRISTREGEL_LABEL: Record<Fristregel, string> = {
  sofort: 'sofort',
  monatsende: 'auf Monatsende',
  quartalsende: 'auf Quartalsende',
  x_tage: 'nach x Tagen',
};

type NumKey = 'fristregelTage' | 'uebergabeTageVorEnddatum' | 'portalNachlaufTage' | 'aufbewahrungJahre';

const NUM_FIELDS: Array<{ key: NumKey; label: string; hint: string }> = [
  { key: 'uebergabeTageVorEnddatum', label: 'Übergabe-Aufgabe Tage vor Enddatum', hint: 'Wann die Übergabe vorbereitet wird.' },
  { key: 'portalNachlaufTage', label: 'Portalzugang Tage nach Enddatum', hint: 'So lange sieht der Kunde seine Unterlagen noch im Portal.' },
  { key: 'aufbewahrungJahre', label: 'Aufbewahrung Jahre', hint: 'Ab dem Enddatum.' },
];

function useConfig() {
  return useQuery<MandateTerminationConfigDto>({
    queryKey: qk.adminMandateTermination(),
    queryFn: () => apiClient.mandateTermination.config(),
  });
}

export function MandatsaufloesungPage() {
  // P6-S3: the Dankesbrief texts (P-06.S9 / S9A) are edited here, next to the settings.
  const texts = useTextTemplates('P-06');
  const config = useConfig();
  const qc = useQueryClient();
  const save = useMutation<MandateTerminationConfigDto, Error, SetMandateTerminationConfigBody>({
    mutationFn: (body) => apiClient.mandateTermination.setConfig(body),
    onSuccess: (saved) => {
      qc.setQueryData(qk.adminMandateTermination(), saved);
      setEdits({});
    },
  });
  const { can } = useEffectivePermissions();
  const canManage = can('tenant.manage');
  const [edits, setEdits] = useState<Partial<Record<NumKey, string>> & { fristregel?: Fristregel }>({});

  if (config.isError) {
    return (
      <div>
        <AdminPageHeader title={meta.title} description={meta.description} />
        <p className="flex items-center gap-2 text-sm text-destructive" role="alert">
          <AlertTriangle className="size-4" />
          Die Einstellungen konnten nicht geladen werden.
        </p>
      </div>
    );
  }
  if (config.data === undefined) {
    return (
      <div>
        <AdminPageHeader title={meta.title} description={meta.description} />
        <p className="flex items-center gap-2 text-sm text-muted-foreground" role="status">
          <Loader2 className="size-4 animate-spin" /> Lädt …
        </p>
      </div>
    );
  }
  const data = config.data;
  const fristregel = edits.fristregel ?? data.fristregel;
  const raw = (k: NumKey) => edits[k] ?? String(data[k]);
  const num = (k: NumKey) => Number(raw(k));
  const valid = (k: NumKey) => {
    const n = num(k);
    return raw(k).trim() !== '' && Number.isInteger(n) && n >= B[k].min && n <= B[k].max;
  };
  const allValid = (Object.keys(B) as NumKey[]).every(valid);

  function onSave() {
    save.mutate({
      fristregel,
      fristregelTage: num('fristregelTage'),
      uebergabeTageVorEnddatum: num('uebergabeTageVorEnddatum'),
      portalNachlaufTage: num('portalNachlaufTage'),
      aufbewahrungJahre: num('aufbewahrungJahre'),
    });
  }

  const numberInput = (k: NumKey, label: string) => (
    <input
      type="number"
      aria-label={label}
      className="w-24 rounded border px-2 py-1 text-sm"
      min={B[k].min}
      max={B[k].max}
      value={raw(k)}
      disabled={!canManage}
      onChange={(e) => setEdits((p) => ({ ...p, [k]: e.target.value }))}
    />
  );
  const boundsHint = (k: NumKey) =>
    !valid(k) ? (
      <span className="text-xs text-destructive">
        Erlaubt: {B[k].min} bis {B[k].max}
      </span>
    ) : null;

  return (
    <div>
      <AdminPageHeader title={meta.title} description={meta.description} />
      <section className="flex flex-col gap-4 rounded-lg border bg-card p-4" aria-label="Mandatsauflösung">
        {data.istDefault && (
          <span className="w-fit rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground" data-testid="mta-standard">
            Standard
          </span>
        )}
        <label className="flex flex-col gap-1 text-sm">
          Fristregel
          <div className="flex items-center gap-2">
            <select
              className="w-56 rounded border px-2 py-1 text-sm"
              aria-label="Fristregel"
              value={fristregel}
              disabled={!canManage}
              onChange={(e) => setEdits((p) => ({ ...p, fristregel: e.target.value as Fristregel }))}
            >
              {FRISTREGEL_VALUES.map((v) => (
                <option key={v} value={v}>
                  {FRISTREGEL_LABEL[v]}
                </option>
              ))}
            </select>
            {fristregel === 'x_tage' && numberInput('fristregelTage', 'Tage')}
          </div>
          {fristregel === 'x_tage' && boundsHint('fristregelTage')}
          <span className="text-xs text-muted-foreground">
            Das Enddatum einer Kündigung ohne Datum. Der Dialog zeigt es als «abgeleitet», der Broker bestätigt es.
          </span>
        </label>
        {NUM_FIELDS.map((f) => (
          <label key={f.key} className="flex flex-col gap-1 text-sm">
            {f.label}
            {numberInput(f.key, f.label)}
            {boundsHint(f.key)}
            <span className="text-xs text-muted-foreground">{f.hint}</span>
          </label>
        ))}
        {save.isError && (
          <p className="text-sm text-destructive" role="alert">
            Die Einstellungen konnten nicht gespeichert werden.
          </p>
        )}
        {canManage && (
          <Button size="sm" className="w-fit" disabled={save.isPending || !allValid} onClick={onSave}>
            Speichern
          </Button>
        )}
      </section>
      {texts.data && <TextTemplatesEditor templates={texts.data} processKey="P-06" />}
    </div>
  );
}
